import { useState, useRef, useCallback, useEffect } from "react";
import { Download, Share2, X, ChevronLeft, ChevronRight, Image as ImageIcon, Mic, Music, Brain } from "lucide-react";
import { Button } from "./Button";
import { toast } from "sonner";
import QRCode from "qrcode";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const APP_URL = window.location.origin;

// Nature background images (high-quality, royalty-free)
const NATURE_BACKGROUNDS = [
  {
    id: "forest",
    name: "Misty Forest",
    url: "https://images.unsplash.com/photo-1448375240586-882707db888b?w=1080&q=80",
    overlay: "rgba(45, 74, 42, 0.55)"
  },
  {
    id: "ocean",
    name: "Ocean Sunrise",
    url: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1080&q=80",
    overlay: "rgba(30, 58, 95, 0.50)"
  },
  {
    id: "mountains",
    name: "Mountain Peaks",
    url: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=1080&q=80",
    overlay: "rgba(55, 65, 81, 0.50)"
  },
  {
    id: "meadow",
    name: "Golden Meadow",
    url: "https://images.unsplash.com/photo-1501854140801-50d01698950b?w=1080&q=80",
    overlay: "rgba(74, 103, 65, 0.45)"
  },
  {
    id: "lake",
    name: "Serene Lake",
    url: "https://images.unsplash.com/photo-1439066615861-d1af74d74000?w=1080&q=80",
    overlay: "rgba(30, 41, 59, 0.50)"
  }
];

// Inspirational headers for the card
const CARD_HEADERS = [
  "This is my voice. This is my truth.",
  "I speak transformation into being.",
  "My voice carries my intention.",
  "Words of power, in my own voice.",
  "I am the author of my reality."
];

/**
 * Share Installation Card
 * Generates a shareable image card with the affirmation and installation details
 */
export const ShareInstallationCard = ({ 
  installation,
  onClose,
  isVisible = false 
}) => {
  const canvasRef = useRef(null);
  const [currentHeaderIndex, setCurrentHeaderIndex] = useState(0);
  const [currentBgIndex, setCurrentBgIndex] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [logoImage, setLogoImage] = useState(null);
  const [backgroundImages, setBackgroundImages] = useState({});
  const [loadingBg, setLoadingBg] = useState(true);
  const [referralCode, setReferralCode] = useState(null);

  const currentHeader = CARD_HEADERS[currentHeaderIndex];
  const currentBackground = NATURE_BACKGROUNDS[currentBgIndex];

  // Load the Reclaim logo
  useEffect(() => {
    if (isVisible && !logoImage) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => setLogoImage(img);
      img.onerror = () => console.log("Logo load error");
      img.src = "/assets/reclaim-logo-white.png";
    }
  }, [isVisible, logoImage]);

  // Fetch user's referral code
  useEffect(() => {
    if (isVisible && !referralCode) {
      const token = localStorage.getItem("reclaim_token");
      if (token) {
        axios.get(`${API_URL}/api/referral/code`, {
          headers: { Authorization: `Bearer ${token}` }
        })
          .then(res => setReferralCode(res.data?.code))
          .catch(() => {}); // Silently fail
      }
    }
  }, [isVisible, referralCode]);

  // Preload background images
  useEffect(() => {
    if (isVisible) {
      setLoadingBg(true);
      const loadedImages = {};
      let loadedCount = 0;
      
      NATURE_BACKGROUNDS.forEach((bg) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          loadedImages[bg.id] = img;
          loadedCount++;
          if (loadedCount === NATURE_BACKGROUNDS.length) {
            setBackgroundImages(loadedImages);
            setLoadingBg(false);
          }
        };
        img.onerror = () => {
          loadedCount++;
          if (loadedCount === NATURE_BACKGROUNDS.length) {
            setBackgroundImages(loadedImages);
            setLoadingBg(false);
          }
        };
        img.src = bg.url;
      });
    }
  }, [isVisible]);

  // Generate the card image on canvas
  const generateCard = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas || !installation) return null;

    const ctx = canvas.getContext('2d');
    const width = 1080;
    const height = 1920;
    
    canvas.width = width;
    canvas.height = height;

    // Draw background image or fallback gradient
    const bgImage = backgroundImages[currentBackground.id];
    if (bgImage) {
      const imgRatio = bgImage.width / bgImage.height;
      const canvasRatio = width / height;
      
      let drawWidth, drawHeight, offsetX = 0, offsetY = 0;
      
      if (imgRatio > canvasRatio) {
        drawHeight = height;
        drawWidth = height * imgRatio;
        offsetX = -(drawWidth - width) / 2;
      } else {
        drawWidth = width;
        drawHeight = width / imgRatio;
        offsetY = -(drawHeight - height) / 2;
      }
      
      ctx.drawImage(bgImage, offsetX, offsetY, drawWidth, drawHeight);
      
      // Apply color overlay
      ctx.fillStyle = currentBackground.overlay;
      ctx.fillRect(0, 0, width, height);
      
      // Add vignette
      const vignette = ctx.createRadialGradient(width/2, height/2, 0, width/2, height/2, height * 0.7);
      vignette.addColorStop(0, 'rgba(0,0,0,0)');
      vignette.addColorStop(1, 'rgba(0,0,0,0.3)');
      ctx.fillStyle = vignette;
      ctx.fillRect(0, 0, width, height);
    } else {
      // Fallback gradient
      const gradient = ctx.createLinearGradient(0, 0, 0, height);
      gradient.addColorStop(0, '#2D4A2A');
      gradient.addColorStop(1, '#1A2D18');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
    }

    // Add subtle grain texture
    ctx.globalAlpha = 0.04;
    for (let i = 0; i < 8000; i++) {
      ctx.fillStyle = Math.random() > 0.5 ? '#FFFFFF' : '#000000';
      ctx.fillRect(Math.random() * width, Math.random() * height, 1, 1);
    }
    ctx.globalAlpha = 1;

    // Draw logo
    if (logoImage) {
      const logoHeight = 50;
      const logoWidth = (logoImage.width / logoImage.height) * logoHeight;
      const logoX = (width - logoWidth) / 2;
      const logoY = 140;
      ctx.drawImage(logoImage, logoX, logoY, logoWidth, logoHeight);
      
      // Tagline under logo
      ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
      ctx.font = '400 24px "Inter", sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('School of Consciousness', width / 2, logoY + logoHeight + 35);
    } else {
      // Fallback text logo
      ctx.fillStyle = '#FFFFFF';
      ctx.font = '600 56px "Playfair Display", Georgia, serif';
      ctx.textAlign = 'center';
      ctx.fillText('RECLAIM', width / 2, 190);
      
      ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
      ctx.font = '400 24px "Inter", sans-serif';
      ctx.fillText('School of Consciousness', width / 2, 240);
    }

    // Voice Studio badge
    ctx.fillStyle = 'rgba(212, 163, 115, 0.9)';
    ctx.font = '500 22px "Inter", sans-serif';
    ctx.textAlign = 'center';
    const badgeY = 320;
    ctx.fillText('VOICE STUDIO', width / 2, badgeY);
    
    // Decorative line under badge
    ctx.strokeStyle = 'rgba(212, 163, 115, 0.5)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(width / 2 - 80, badgeY + 15);
    ctx.lineTo(width / 2 + 80, badgeY + 15);
    ctx.stroke();

    // Header text
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'italic 400 38px "Playfair Display", Georgia, serif';
    ctx.textAlign = 'center';
    ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
    ctx.shadowBlur = 15;
    ctx.shadowOffsetY = 3;
    ctx.fillText(currentHeader, width / 2, 450);
    ctx.shadowBlur = 0;

    // Main affirmation text in a quote box
    const boxPadding = 60;
    const boxWidth = width - 120;
    const boxX = (width - boxWidth) / 2;
    const boxY = 520;
    
    // Quote box background
    ctx.fillStyle = 'rgba(255, 255, 255, 0.12)';
    ctx.beginPath();
    ctx.roundRect(boxX, boxY, boxWidth, 380, 24);
    ctx.fill();

    // Large quote mark
    ctx.fillStyle = 'rgba(212, 163, 115, 0.7)';
    ctx.font = '300 140px Georgia, serif';
    ctx.textAlign = 'left';
    ctx.fillText('"', boxX + 30, boxY + 100);

    // Affirmation text
    ctx.fillStyle = '#FFFFFF';
    ctx.font = '500 48px "Playfair Display", Georgia, serif';
    ctx.textAlign = 'center';
    ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
    ctx.shadowBlur = 10;
    
    const affirmationLines = wrapText(ctx, installation.affirmation_text, boxWidth - 100, 48);
    let yPos = boxY + 160;
    affirmationLines.slice(0, 4).forEach(line => {
      ctx.fillText(line, width / 2, yPos);
      yPos += 65;
    });
    ctx.shadowBlur = 0;

    // Installation details section
    const detailsY = 980;
    
    // Voice Style
    ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
    ctx.font = '400 26px "Inter", sans-serif';
    ctx.textAlign = 'center';
    
    const voiceStyleName = installation.voice_style_name || "Calm Meditation";
    const backgroundName = installation.background_audio_name || "Silence";
    const binauralName = installation.binaural_beat !== "none" ? installation.binaural_beat_name : null;

    // Draw detail badges
    const badgeHeight = 50;
    const badgeSpacing = 20;
    let detailY = detailsY;

    // Voice style badge
    drawDetailBadge(ctx, width / 2, detailY, `Voice: ${voiceStyleName}`, "#4A6741");
    detailY += badgeHeight + badgeSpacing;

    // Background audio badge (if not silence)
    if (backgroundName !== "Silence") {
      drawDetailBadge(ctx, width / 2, detailY, `Background: ${backgroundName}`, "#5B7652");
      detailY += badgeHeight + badgeSpacing;
    }

    // Binaural badge (if selected)
    if (binauralName) {
      drawDetailBadge(ctx, width / 2, detailY, `Brainwave: ${binauralName}`, "#6B8663");
      detailY += badgeHeight + badgeSpacing;
    }

    // QR Code Section
    const qrY = 1280;
    
    // Generate QR code for the listen URL with referral code
    const listenUrl = referralCode 
      ? `${APP_URL}/listen/${installation.id}?ref=${referralCode}`
      : `${APP_URL}/listen/${installation.id}`;
    try {
      const qrDataUrl = await QRCode.toDataURL(listenUrl, {
        width: 200,
        margin: 0,
        color: {
          dark: '#FFFFFF',
          light: '#00000000'  // Transparent background
        },
        errorCorrectionLevel: 'M'
      });
      
      // Draw QR code background circle
      ctx.fillStyle = 'rgba(255, 255, 255, 0.15)';
      ctx.beginPath();
      ctx.arc(width / 2, qrY + 100, 130, 0, Math.PI * 2);
      ctx.fill();
      
      // Draw QR code
      const qrImage = new Image();
      await new Promise((resolve, reject) => {
        qrImage.onload = resolve;
        qrImage.onerror = reject;
        qrImage.src = qrDataUrl;
      });
      
      const qrSize = 180;
      ctx.drawImage(qrImage, (width - qrSize) / 2, qrY + 10, qrSize, qrSize);
    } catch (err) {
      console.log("QR code generation error:", err);
    }
    
    // Scan to listen text
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.font = '500 28px "Inter", sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Scan to Listen', width / 2, qrY + 250);
    
    // Referral code if available
    if (referralCode) {
      ctx.fillStyle = 'rgba(212, 163, 115, 0.9)';
      ctx.font = '500 22px "Inter", sans-serif';
      ctx.fillText(`Referral Code: ${referralCode}`, width / 2, qrY + 285);
    }
    
    // Decorative line
    ctx.strokeStyle = 'rgba(212, 163, 115, 0.5)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(width / 2 - 80, qrY + 310);
    ctx.lineTo(width / 2 + 80, qrY + 310);
    ctx.stroke();

    // Call to action
    ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    ctx.font = '400 26px "Inter", sans-serif';
    ctx.textAlign = 'center';
    const ctaText = referralCode ? `Join me at reclaim.app` : 'Create your own at reclaim.app';
    ctx.fillText(ctaText, width / 2, qrY + 355);

    // Branding footer
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.font = '400 22px "Inter", sans-serif';
    ctx.fillText('Shared from Reclaim Voice Studio', width / 2, height - 80);

    return canvas.toDataURL('image/png');
  }, [installation, currentHeader, currentBackground, logoImage, backgroundImages, referralCode]);

  // Helper function to draw detail badges
  const drawDetailBadge = (ctx, x, y, text, color) => {
    const textWidth = ctx.measureText(text).width;
    const padding = 30;
    const badgeWidth = textWidth + padding * 2;
    const badgeHeight = 44;
    
    // Badge background
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.8;
    ctx.beginPath();
    ctx.roundRect(x - badgeWidth / 2, y, badgeWidth, badgeHeight, 22);
    ctx.fill();
    ctx.globalAlpha = 1;
    
    // Badge text
    ctx.fillStyle = '#FFFFFF';
    ctx.font = '500 24px "Inter", sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(text, x, y + 30);
  };

  // Helper function for text wrapping
  const wrapText = (ctx, text, maxWidth, fontSize) => {
    const words = text.split(' ');
    const lines = [];
    let currentLine = '';

    words.forEach(word => {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const metrics = ctx.measureText(testLine);
      
      if (metrics.width > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    });
    
    if (currentLine) {
      lines.push(currentLine);
    }
    
    return lines;
  };

  // Update preview when options change
  const updatePreview = useCallback(async () => {
    if (loadingBg || !installation) return;
    setIsGenerating(true);
    const url = await generateCard();
    setPreviewUrl(url);
    setIsGenerating(false);
  }, [generateCard, loadingBg, installation]);

  // Generate preview on mount and when options change
  useEffect(() => {
    if (isVisible && !loadingBg && installation) {
      updatePreview();
    }
  }, [isVisible, loadingBg, updatePreview, currentBgIndex, currentHeaderIndex, installation]);

  const handleHeaderChange = (direction) => {
    const newIndex = (currentHeaderIndex + direction + CARD_HEADERS.length) % CARD_HEADERS.length;
    setCurrentHeaderIndex(newIndex);
  };

  const handleBackgroundChange = (direction) => {
    const newIndex = (currentBgIndex + direction + NATURE_BACKGROUNDS.length) % NATURE_BACKGROUNDS.length;
    setCurrentBgIndex(newIndex);
  };

  // Download image
  const handleDownload = async () => {
    setIsGenerating(true);
    const dataUrl = await generateCard();
    
    const link = document.createElement('a');
    link.download = 'my-voice-installation.png';
    link.href = dataUrl;
    link.click();
    
    toast.success("Image saved! You can now share it manually.", {
      duration: 4000,
    });
    
    setIsGenerating(false);
  };

  // Share via native share sheet
  const handleShare = async () => {
    setIsGenerating(true);
    
    try {
      const dataUrl = await generateCard();
      
      if (!dataUrl) {
        console.error("Failed to generate card");
        toast.error("Failed to generate share image");
        setIsGenerating(false);
        return;
      }
      
      // Convert data URL to blob
      const response = await fetch(dataUrl);
      const blob = await response.blob();
      const file = new File([blob], 'my-voice-installation.png', { type: 'image/png' });

      // Check if Web Share API with files is supported
      const canShareFiles = navigator.share && navigator.canShare && navigator.canShare({ files: [file] });
      
      if (canShareFiles) {
        try {
          await navigator.share({
            files: [file],
            title: 'My Voice Installation',
            text: `"${installation?.affirmation_text}" - Created with Reclaim Voice Studio`
          });
          setIsGenerating(false);
          return;
        } catch (err) {
          if (err.name === 'AbortError') {
            // User cancelled - that's okay
            setIsGenerating(false);
            return;
          }
          console.log("File share failed, trying URL share:", err);
        }
      }
      
      // Fallback 1: Try sharing just text/URL (more broadly supported)
      if (navigator.share) {
        try {
          const listenUrl = `${window.location.origin}/listen/${installation?.id}`;
          await navigator.share({
            title: 'My Voice Installation - Reclaim',
            text: `"${installation?.affirmation_text}"\n\nListen to my voice installation:`,
            url: listenUrl
          });
          toast.success("Link shared!", { duration: 3000 });
          setIsGenerating(false);
          return;
        } catch (err) {
          if (err.name === 'AbortError') {
            setIsGenerating(false);
            return;
          }
          console.log("URL share failed, falling back to download:", err);
        }
      }
      
      // Fallback 2: Download the image
      const link = document.createElement('a');
      link.download = 'my-voice-installation.png';
      link.href = dataUrl;
      link.click();
      
      toast.success("Image saved to your device! You can now share it manually.", {
        duration: 4000,
      });
      
    } catch (err) {
      console.error("Share error:", err);
      // Last resort: try download
      try {
        const dataUrl = await generateCard();
        if (dataUrl) {
          const link = document.createElement('a');
          link.download = 'my-voice-installation.png';
          link.href = dataUrl;
          link.click();
          
          toast.success("Image saved! You can now share it manually.", {
            duration: 4000,
          });
        }
      } catch (e) {
        console.error("Download fallback failed:", e);
        toast.error("Unable to share. Please try again.");
      }
    }
    
    setIsGenerating(false);
  };

  if (!isVisible || !installation) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] animate-fade-in overflow-y-auto">
      <div className="min-h-full flex items-center justify-center p-4 pb-24">
        <div className="bg-white rounded-3xl max-w-sm w-full shadow-2xl animate-fade-in-up">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-[#E5E7EB]">
            <h3 className="font-heading text-lg text-[#1F2937]">Share Your Installation</h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-[#F3F4F6] rounded-full transition-colors"
              data-testid="close-share-installation-modal"
            >
              <X size={20} className="text-[#6B7280]" />
            </button>
          </div>

        {/* Preview - smaller on mobile */}
        <div className="p-4 space-y-3">
          <div className="relative aspect-[9/16] max-h-[40vh] bg-gradient-to-b from-[#2D4A2A] to-[#1A2D18] rounded-2xl overflow-hidden shadow-inner mx-auto">
            {previewUrl && !loadingBg ? (
              <img 
                src={previewUrl} 
                alt="Share card preview" 
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            
            {/* Header navigation */}
            <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-4">
              <button
                onClick={() => handleHeaderChange(-1)}
                className="w-8 h-8 bg-white/90 backdrop-blur rounded-full flex items-center justify-center shadow-md hover:bg-white transition-colors"
                data-testid="prev-header"
              >
                <ChevronLeft size={18} className="text-[#4A6741]" />
              </button>
              <span className="text-xs text-white/80 bg-black/20 px-3 py-1 rounded-full backdrop-blur">
                {currentHeaderIndex + 1} / {CARD_HEADERS.length}
              </span>
              <button
                onClick={() => handleHeaderChange(1)}
                className="w-8 h-8 bg-white/90 backdrop-blur rounded-full flex items-center justify-center shadow-md hover:bg-white transition-colors"
                data-testid="next-header"
              >
                <ChevronRight size={18} className="text-[#4A6741]" />
              </button>
            </div>
          </div>

          {/* Background selector */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-[#6B7280]">
              <ImageIcon size={16} />
              <span>Background:</span>
            </div>
            <div className="flex-1 flex items-center gap-2">
              <button
                onClick={() => handleBackgroundChange(-1)}
                className="p-1.5 hover:bg-[#F3F4F6] rounded-full transition-colors"
              >
                <ChevronLeft size={16} className="text-[#6B7280]" />
              </button>
              <span className="flex-1 text-center text-sm font-medium text-[#1F2937]">
                {currentBackground.name}
              </span>
              <button
                onClick={() => handleBackgroundChange(1)}
                className="p-1.5 hover:bg-[#F3F4F6] rounded-full transition-colors"
              >
                <ChevronRight size={16} className="text-[#6B7280]" />
              </button>
            </div>
          </div>

          {/* Background thumbnails */}
          <div className="flex gap-2 justify-center">
            {NATURE_BACKGROUNDS.map((bg, idx) => (
              <button
                key={bg.id}
                onClick={() => setCurrentBgIndex(idx)}
                className={`w-12 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                  idx === currentBgIndex 
                    ? 'border-[#4A6741] scale-105' 
                    : 'border-transparent opacity-60 hover:opacity-100'
                }`}
                data-testid={`bg-thumb-${bg.id}`}
              >
                <img 
                  src={bg.url} 
                  alt={bg.name}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>

          {/* Installation details summary */}
          <div className="bg-[#F9FAFB] rounded-xl p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Mic size={14} className="text-[#4A6741]" />
              <span className="text-[#6B7280]">Style:</span>
              <span className="text-[#1F2937] font-medium">{installation.voice_style_name}</span>
            </div>
            {installation.background_audio_name !== "Silence" && (
              <div className="flex items-center gap-2 text-sm">
                <Music size={14} className="text-[#4A6741]" />
                <span className="text-[#6B7280]">Background:</span>
                <span className="text-[#1F2937] font-medium">{installation.background_audio_name}</span>
              </div>
            )}
            {installation.binaural_beat !== "none" && (
              <div className="flex items-center gap-2 text-sm">
                <Brain size={14} className="text-[#4A6741]" />
                <span className="text-[#6B7280]">Brainwave:</span>
                <span className="text-[#1F2937] font-medium">{installation.binaural_beat_name}</span>
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={handleDownload}
              disabled={isGenerating || loadingBg}
              className="flex-1"
              data-testid="download-installation-card-btn"
            >
              <Download size={18} />
              Save
            </Button>
            <Button
              onClick={handleShare}
              disabled={isGenerating || loadingBg}
              className="flex-1"
              data-testid="share-installation-card-btn"
            >
              <Share2 size={18} />
              Share
            </Button>
          </div>

          <p className="text-xs text-center text-[#9CA3AF] pb-2">
            Share to Instagram, WhatsApp, Messages, X & more
          </p>
        </div>

        {/* Hidden canvas for image generation */}
        <canvas ref={canvasRef} className="hidden" />
      </div>
      </div>
    </div>
  );
};

export default ShareInstallationCard;
